import React, { useEffect,useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Typography, Button, Box, Chip, TextField,
  CircularProgress, Snackbar, Alert,
} from '@mui/material';
import { IconButton } from '@mui/material';
import styled, { keyframes, css } from 'styled-components';
import QuizIcon from '@mui/icons-material/Quiz';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CodeIcon from '@mui/icons-material/Code';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import BugReportIcon from '@mui/icons-material/BugReport';
import BuildIcon from '@mui/icons-material/Build';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddIcon from '@mui/icons-material/Add';
import { jsPDF } from 'jspdf';
import ImportQuestionsModal from './importQuestions/ImportQuestionsModal';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { FONT_BODY, FONT_DISPLAY, FONT_MONO, TYPE, RADIUS } from '../../styles/recruiterTheme';

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const shake = keyframes`
  0%,100% { transform: translateX(0); }
  20%,60% { transform: translateX(-4px); }
  40%,80% { transform: translateX(4px); }
`;
const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Dialog Shell ─────────────────────────────────────────────────────────────
const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 24px 24px 0 24px;
`;
const DialogTitleText = styled.div`
  font-family: ${FONT_DISPLAY};
  font-weight: 700;
  font-size: ${TYPE.section}px;
  line-height: 1.25;
  background: linear-gradient(135deg, #2D1B5E 0%, #6750A4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
`;
const DialogSubtitle = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  line-height: 1.5;
  color: #8B7DB5;
  font-weight: 500;
  margin-top: 4px;
`;
const CloseBtn = styled(IconButton)`
  && {
    color: #8B7DB5 !important;
    background: #F3EEF9 !important;
    border-radius: ${RADIUS.sm}px !important;
    width: 36px !important;
    height: 36px !important;
    flex-shrink: 0;
    &:hover { background: #E8DEF8 !important; color: #4F378B !important; }
    svg { font-size: 20px; }
  }
`;

// ─── Edit Mode Banner ─────────────────────────────────────────────────────────
const EditBanner = styled.div`
  margin: 12px 24px 0;
  padding: 8px 16px;
  background: linear-gradient(135deg, #FFF8E1, #FFF3CD);
  border: 1.5px solid #FFD54F;
  border-radius: ${RADIUS.md}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  ${css`animation: ${slideIn} 0.25s ease both;`}
`;
const EditBannerText = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  line-height: 1.5;
  color: #7B5E00;
  display: flex;
  align-items: center;
  gap: 8px;
`;
const EditBannerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px 24px 0 24px;
`;
const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 16px;
  border-radius: ${RADIUS.sm}px;
  border: none;
  cursor: pointer;
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  line-height: 1;
  white-space: nowrap;
  transition: all 0.2s ease;
  background: ${p => p.$active ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#F3EEF9'};
  color: ${p => p.$active ? '#fff' : '#8B7DB5'};
  box-shadow: ${p => p.$active ? '0 4px 14px rgba(79,55,139,0.3)' : 'none'};
  transform: ${p => p.$active ? 'translateY(-1px)' : 'none'};
  &:hover {
    background: ${p => p.$active ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#E8DEF8'};
    color: ${p => p.$active ? '#fff' : '#4F378B'};
  }
  svg { font-size: 16px; }
`;
const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  border-radius: ${RADIUS.pill}px;
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  padding: 0 4px;
  background: ${p => p.$active ? 'rgba(255,255,255,0.25)' : 'rgba(103,80,164,0.12)'};
  color: ${p => p.$active ? '#fff' : '#6750A4'};
`;

// ─── Tab Content ──────────────────────────────────────────────────────────────
const TabContent = styled.div`
  padding: 16px 24px 8px 24px;
  max-height: 56vh;
  overflow-y: auto;
  ${css`animation: ${fadeUp} 0.28s ease both;`}
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #F3EEF9; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #D0BCFF; border-radius: 4px; }
`;

// ─── Question Card ────────────────────────────────────────────────────────────
const QuestionCard = styled.div`
  background: ${p => p.$selected ? '#FFF8E1' : p.$deleted ? '#FFF5F5' : '#FDFBFF'};
  border: ${p => p.$selected ? '1.5px solid #FFD54F' : p.$deleted ? '1.5px dashed #FFCDD2' : '1px solid #EDE7F6'};
  opacity: ${p => p.$deleted ? 0.5 : 1};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  margin-bottom: 12px;
  transition: box-shadow 0.18s, border-color 0.18s, background 0.18s, opacity 0.18s;
  opacity: ${p => p.deleted ? 0.5 : 1};
  position: relative;
  ${p => p.selected && css`animation: none;`}
  &:hover {
    box-shadow: ${p => p.deleted ? 'none' : '0 4px 18px rgba(79,55,139,0.1)'};
    border-color: ${p => p.deleted ? '#FFCDD2' : p.selected ? '#FFD54F' : '#D0BCFF'};
  }
`;
const DeletedOverlay = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  height: 20px;
  background: #FFCDD2;
  color: #B71C1C;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const QuestionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${RADIUS.sm}px;
  background: ${p => p.$deleted ? '#FFCDD2' : 'linear-gradient(135deg, #4F378B, #6750A4)'};
  color: ${p => p.$deleted ? '#B71C1C' : '#fff'};
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.caption}px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  margin-right: 12px;
  margin-top: -1px;
  transition: all 0.2s;
`;
const QuestionText = styled.div`
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.body}px;
  color: #2D1B5E;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  min-width: 0;
`;

// ─── Edit Mode Controls ───────────────────────────────────────────────────────
const CardEditRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
`;
const SelectCheckbox = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  background: ${p => p.checked ? '#FFF3CD' : '#F3EEF9'};
  border: 1.5px solid ${p => p.checked ? '#FFD54F' : '#D0BCFF'};
  border-radius: ${RADIUS.sm}px;
  padding: 0 12px;
  cursor: pointer;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: ${p => p.checked ? '#7B5E00' : '#6750A4'};
  transition: all 0.15s;
  &:hover { border-color: #6750A4; }
  svg { font-size: 16px; }
`;
const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  background: ${p => p.$deleted ? '#E8F5E9' : '#FFEBEE'};
  border: 1.5px solid ${p => p.$deleted ? '#A5D6A7' : '#FFCDD2'};
  border-radius: ${RADIUS.sm}px;
  padding: 0 12px;
  cursor: pointer;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: ${p => p.$deleted ? '#2E7D32' : '#B71C1C'};
  transition: all 0.15s;
  &:hover {
    background: ${p => p.deleted ? '#C8E6C9' : '#FFCDD2'};
    ${p => !p.deleted && css`animation: ${shake} 0.3s ease;`}
  }
  svg { font-size: 16px; }
`;

// ─── Reason / sub-dialog text ─────────────────────────────────────────────────
const ReasonTitle = styled.div`
  font-family: ${FONT_DISPLAY};
  font-weight: 700;
  font-size: ${TYPE.section}px;
  line-height: 1.25;
  letter-spacing: -0.02em;
  color: #2D1B5E;
`;
const ReasonSubtitle = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  color: #8B7DB5;
  margin-bottom: 16px;
  line-height: 1.5;
`;
const ReasonPresets = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;
const PresetChip = styled.button`
  display: inline-flex;
  align-items: center;
  height: 32px;
  background: ${p => p.active ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#F3EEF9'};
  color: ${p => p.active ? '#fff' : '#6750A4'};
  border: 1.5px solid ${p => p.active ? 'transparent' : '#D0BCFF'};
  border-radius: ${RADIUS.pill}px;
  padding: 0 12px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #6750A4; }
`;

// ─── Deletion Log Panel ───────────────────────────────────────────────────────
const LogPanel = styled.div`
  background: #FDFBFF;
  border: 1px solid #EDE7F6;
  border-radius: ${RADIUS.md}px;
  padding: 12px 16px;
  margin-bottom: 12px;
  ${css`animation: ${fadeUp} 0.2s ease both;`}
`;
const LogEntry = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #F3EEF9;
  &:last-child { border-bottom: none; padding-bottom: 0; }
`;
const LogIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${RADIUS.sm}px;
  background: #FFEBEE;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg { font-size: 14px; color: #B71C1C; }
`;
const LogMeta = styled.div`
  flex: 1;
  min-width: 0;
`;
const LogQuestion = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  color: #2D1B5E;
  line-height: 1.5;
  margin-bottom: 4px;
  /* truncate */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const LogReason = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  line-height: 1.5;
  color: #8B7DB5;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;
const LogTimestamp = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  color: #B0A0D0;
  font-weight: 500;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  margin-top: 4px;
`;

// ─── MCQ Options ─────────────────────────────────────────────────────────────
const OptionsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0 0 36px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const OptionItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  min-height: 40px;
  box-sizing: border-box;
  padding: 8px 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid ${p => p.$correct  ? '#A5D6A7' : '#EDE7F6'};
  background: ${p => p.$correct  ? 'linear-gradient(135deg, #E8F5E9, #F1FBF2)' : '#F3EEF9'};
  color: ${p => p.$correct  ? '#1B6B3A' : '#5C4A8A'};
  line-height: 1.5;
  &::before {
    content: attr(data-label);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: ${p => p.correct ? '#A5D6A7' : '#E8DEF8'};
    color: ${p => p.correct ? '#1B6B3A' : '#4F378B'};
    font-size: ${TYPE.overline}px;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
  }
`;
const CorrectBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  margin-left: auto;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  color: #2E7D32;
  background: #C8E6C9;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  flex-shrink: 0;
  svg { font-size: 12px; }
`;
const CodeBlock = styled.pre`
  margin: 8px 0 0 36px;
  border-radius: ${RADIUS.sm}px;
  padding: 12px 16px;
  font-family: ${FONT_MONO};
  font-size: ${TYPE.small}px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  background: #1e1e2e;
  color: #cdd6f4;
  border: 1px solid #313244;
  overflow-x: auto;
`;
const InfoBox = styled.div`
  margin: 8px 0 0 36px;
  border-radius: ${RADIUS.sm}px;
  padding: 12px 16px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 500;
  line-height: 1.5;
  white-space: pre-wrap;
  border: 1px solid ${p => p.borderColor || '#E0E0E0'};
  background: ${p => p.bg || '#F9F9F9'};
  color: ${p => p.textColor || '#333'};
`;
const InfoBoxLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.5;
  color: ${p => p.color || '#4F378B'};
  margin: 16px 0 0 36px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  svg { font-size: 14px; }
`;
const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  white-space: nowrap;
  background: ${p => p.bg || '#EDE7F6'};
  color: ${p => p.color || '#4F378B'};
`;
const LangBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  background: linear-gradient(135deg, #E8DEF8, #D0BCFF);
  color: #4F378B;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  white-space: nowrap;
  svg { font-size: 12px; }
`;
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
  color: #B0A0D0;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 500;
  line-height: 1.5;
  gap: 12px;
  svg { font-size: 40px; opacity: 0.4; }
`;
const DifficultyChip = styled(Chip)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    height: 24px !important;
    border-radius: ${RADIUS.pill}px !important;
  }
`;
const SkillChip = styled(Chip)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    height: 24px !important;
    border-radius: ${RADIUS.pill}px !important;
    background: #EDE7F6 !important;
    color: #4F378B !important;
  }
`;
const DownloadBtn = styled(Button)`
  && {
    background: transparent !important;
    color: #6750A4 !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 600 !important;
    font-size: ${TYPE.small}px !important;
    border-radius: ${RADIUS.sm}px !important;
    height: 36px;
    padding: 0 16px !important;
    text-transform: none !important;
    border: 1.5px solid rgba(103,80,164,0.35) !important;
    transition: all 0.2s !important;
    &:hover {
      background: rgba(103,80,164,0.06) !important;
      border-color: #6750A4 !important;
      transform: translateY(-1px);
    }
    svg { font-size: 16px; }
  }
`;

// ─── Regenerate / Add-manual sub-dialogs ──────────────────────────────────────
const RegenerateCard = styled.div`
  background: #fff;
  border-radius: ${RADIUS.lg}px;
  padding: 24px;
  width: 460px;
  max-width: 92vw;
  box-shadow: 0 24px 60px rgba(79,55,139,0.25);
`;
const CountInput = styled.input`
  width: 72px;
  height: 32px;
  box-sizing: border-box;
  padding: 0 8px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid #D0BCFF;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #2D1B5E;
  text-align: center;
  outline: none;
  &:focus { border-color: #6750A4; }
`;
const RegenerateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #F3EEF9;
  &:last-of-type { border-bottom: none; }
`;
const RegenerateLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  color: #2D1B5E;
`;
const AddManualCard = styled.div`
  background: #fff;
  border-radius: ${RADIUS.lg}px;
  padding: 24px;
  width: 620px;
  max-width: 94vw;
  max-height: 88vh;
  overflow-y: auto;
  box-sizing: border-box;
  box-shadow: 0 24px 60px rgba(79,55,139,0.25);
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #D0BCFF; border-radius: 4px; }
`;
const FormSection = styled.div`
  margin-bottom: 16px;
`;
const FormLabel = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  line-height: 1.5;
  color: #4F378B;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;
const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid #D0BCFF;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  color: #2D1B5E;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  line-height: 1.5;
  &:focus { border-color: #6750A4; }
  &::placeholder { color: #B0A0D0; }
`;
const StyledSelect = styled.select`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid #D0BCFF;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  color: #2D1B5E;
  outline: none;
  background: #fff;
  cursor: pointer;
  &:focus { border-color: #6750A4; }
`;
const StyledInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid #D0BCFF;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.body}px;
  color: #2D1B5E;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #6750A4; }
  &::placeholder { color: #B0A0D0; }
`;
const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;
const CorrectToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid ${p => p.correct ? '#A5D6A7' : '#D0BCFF'};
  background: ${p => p.correct ? '#E8F5E9' : '#F3EEF9'};
  color: ${p => p.correct ? '#2E7D32' : '#8B7DB5'};
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s;
  svg { font-size: 14px; }
`;
const AddOptionBtn = styled.button`
  height: 36px;
  background: #F3EEF9;
  border: 1.5px dashed #D0BCFF;
  border-radius: ${RADIUS.sm}px;
  padding: 0 16px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.small}px;
  font-weight: 600;
  color: #6750A4;
  cursor: pointer;
  width: 100%;
  transition: all 0.15s;
  &:hover { background: #E8DEF8; }
`;
const TypeTabBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;
const TypeTab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: ${RADIUS.sm}px;
  border: 1.5px solid ${p => p.active ? '#6750A4' : '#D0BCFF'};
  background: ${p => p.active ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#F3EEF9'};
  color: ${p => p.active ? '#fff' : '#8B7DB5'};
  font-family: ${FONT_BODY};
  font-weight: 600;
  font-size: ${TYPE.small}px;
  cursor: pointer;
  transition: all 0.2s;
  svg { font-size: 16px; }
`;
const ManualBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  background: #E3F2FD;
  color: #1565C0;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  flex-shrink: 0;
  svg { font-size: 12px; }
`;
const DifficultyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 12px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  text-transform: capitalize;
  white-space: nowrap;
  flex-shrink: 0;

  background: ${({ difficulty }) => {
    switch ((difficulty || '').toLowerCase()) {
      case 'easy':
        return '#E8F5E9';
      case 'medium':
        return '#FFF8E1';
      case 'hard':
        return '#FDECEA';
      default:
        return '#F3F4F6';
    }
  }};

  color: ${({ difficulty }) => {
    switch ((difficulty || '').toLowerCase()) {
      case 'easy':
        return '#2E7D32';
      case 'medium':
        return '#EF6C00';
      case 'hard':
        return '#C62828';
      default:
        return '#374151';
    }
  }};
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const cleanText = (text) => (text ? text.replace(/%æ\s*/g, '').trim() : '');
const optionLabel = (idx) => String.fromCharCode(65 + idx);
const cleanTrailingDashes = (text) => text ? String(text).replace(/\s*---\s*$/, '').trim() : '';

const difficultyColors = {
  hard:   { bg: '#FFEBEE', color: '#c62828' },
  medium: { bg: '#FFF3E0', color: '#e65100' },
  easy:   { bg: '#E8F5E9', color: '#2e7d32' },
};
const categoryConfig = {
  Coding:     { bg: '#E3F2FD', color: '#1565C0', icon: <CodeIcon sx={{ fontSize: 13 }} /> },
  Conceptual: { bg: '#F3E5F5', color: '#6A1B9A', icon: <LightbulbIcon sx={{ fontSize: 13 }} /> },
  Debugging:  { bg: '#FFEBEE', color: '#B71C1C', icon: <BugReportIcon sx={{ fontSize: 13 }} /> },
  Tooling:    { bg: '#E8F5E9', color: '#1B5E20', icon: <BuildIcon sx={{ fontSize: 13 }} /> },
};

const REASON_PRESETS = [
  'Not relevant to role',
  'Too advanced',
  'Too basic',
  'Duplicate question',
  'Ambiguous wording',
  'Out of scope',
  'Language mismatch',
  'Other',
];

const stripHtml = (html) =>
  html ? html
    .replace(/<strong>(.*?)<\/strong>/gi, '$1')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
  : '';

const stripCodeFences = (text) => {
  if (!text) return '';
  return text.replace(/```[\w]*\n?/g, '').replace(/```/g, '').trim();
};

const isCorrectOption = (opt, correctAnswer) => {
  if (!correctAnswer) return false;
  const optClean = cleanText(opt).toLowerCase();
  if (Array.isArray(correctAnswer))
    return correctAnswer.some(a => optClean.includes(a.toLowerCase()) || a.toLowerCase().includes(optClean));
  return optClean.includes(String(correctAnswer).toLowerCase()) ||
    String(correctAnswer).toLowerCase().includes(optClean);
};

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
    ' · ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const REMOVAL_BASE = process.env.REACT_APP_REMOVAL_LOG_URL;

const emptySets = () => ({ mcq: new Set(), subjective: new Set(), coding: new Set() });
const emptyCounts = () => ({ mcq: 0, subjective: 0, coding: 0 });

// Backend expects 'Mcq' | 'Subjective' | 'Coding'
const toTabName = (tab) => tab.charAt(0).toUpperCase() + tab.slice(1);

// Read a JSON body without throwing on empty / non-JSON responses
const readJson = async (res) => {
  try { return await res.json(); } catch { return {}; }
};



// ─── Reason Modal Component ───────────────────────────────────────────────────
const ReasonPrompt = ({ count, onConfirm, onCancel, removing = false, error = '' }) => {
  const [reason, setReason] = useState('');
  const [preset, setPreset] = useState('');

  const handlePreset = (p) => {
    setPreset(p);
    setReason(p !== 'Other' ? p : '');
  };

  const finalReason = reason.trim();
  const canConfirm = !!finalReason && !removing;

  return (
    <Dialog
      open
      onClose={() => { if (!removing) onCancel(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${RADIUS.lg}px`,
          boxShadow: '0 24px 60px rgba(183,28,28,0.2)',
        },
      }}
    >
      <Box sx={{ p: 3 }} onClick={e => e.stopPropagation()}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmberIcon sx={{ color: '#E65100', fontSize: 20 }} />
          <ReasonTitle>Remove {count} Question{count > 1 ? 's' : ''}?</ReasonTitle>
        </Box>

        <ReasonSubtitle>
          Please select or write a reason. This will be logged for future reference.
        </ReasonSubtitle>

        <Alert severity="warning" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.small }}>
          Removal is permanent. The question{count > 1 ? 's' : ''} will be deleted from this JD's
          question bank and cannot be restored.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${RADIUS.sm}px`, fontFamily: FONT_BODY, fontSize: TYPE.small }}>
            {error}
          </Alert>
        )}

        <ReasonPresets>
          {REASON_PRESETS.map(p => (
            <PresetChip key={p} active={preset === p} onClick={() => !removing && handlePreset(p)}>
              {p}
            </PresetChip>
          ))}
        </ReasonPresets>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          autoFocus={preset === 'Other'}
          placeholder="Add a custom reason or more context…"
          value={reason}
          disabled={removing}
          onChange={e => setReason(e.target.value)}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: `${RADIUS.sm}px`,
              fontFamily: FONT_BODY,
              fontSize: TYPE.body,
              '& fieldset': { borderColor: '#D0BCFF' },
              '&:hover fieldset': { borderColor: '#6750A4' },
              '&.Mui-focused fieldset': { borderColor: '#6750A4' },
            },
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={removing} sx={{
            fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
            color: '#8B7DB5', border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`,
            textTransform: 'none', px: 2,
          }}>
            Cancel
          </Button>
          <Button
            disabled={!canConfirm}
            onClick={() => onConfirm(finalReason)}
            sx={{
              background: canConfirm ? 'linear-gradient(135deg, #B71C1C, #E53935)' : '#EEE',
              color: canConfirm ? '#fff' : '#aaa',
              fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
              borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
              boxShadow: canConfirm ? '0 4px 14px rgba(183,28,28,0.3)' : 'none',
            }}
          >
            {removing
              ? <CircularProgress size={16} sx={{ color: '#aaa', mr: 1 }} />
              : <DeleteIcon sx={{ fontSize: 16, mr: 0.5 }} />}
            {removing ? 'Removing…' : 'Confirm Remove'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

// ─── Deletion Log View ────────────────────────────────────────────────────────
const DeletionLogView = ({ log, onDownload, loading = false, error = '', onRetry }) => {
  if (loading)
    return (
      <EmptyState>
        <CircularProgress size={28} sx={{ color: '#6750A4' }} />
        Loading removal log…
      </EmptyState>
    );

  if (error)
    return (
      <EmptyState>
        <WarningAmberIcon sx={{ color: '#E65100' }} />
        {error}
        {onRetry && (
          <Button
            onClick={onRetry}
            size="small"
            sx={{
              color: '#6750A4', fontFamily: FONT_BODY, fontWeight: 600, height: 32,
              fontSize: TYPE.caption, borderRadius: `${RADIUS.sm}px`, textTransform: 'none',
              border: '1.5px solid rgba(103,80,164,0.35)', px: 2,
            }}
          >
            Retry
          </Button>
        )}
      </EmptyState>
    );

  if (!log || log.length === 0)
    return (
      <EmptyState>
        <HistoryIcon />
        No questions removed yet. Removed questions will appear here with their reasons.
      </EmptyState>
    );

  const grouped = log.reduce((acc, entry) => {
    const tab = entry.tab || 'Unknown';
    if (!acc[tab]) acc[tab] = [];
    acc[tab].push(entry);
    return acc;
  }, {});

  return (
    <>
      {/* Download button at top of log tab */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          onClick={onDownload}
          startIcon={<DownloadIcon />}
          size="small"
          sx={{
            background: 'transparent',
            color: '#6750A4',
            fontFamily: FONT_BODY,
            fontWeight: 600,
            fontSize: TYPE.caption,
            height: 32,
            borderRadius: `${RADIUS.sm}px`,
            textTransform: 'none',
            border: '1.5px solid rgba(103,80,164,0.35)',
            px: 2,
            '&:hover': {
              background: 'rgba(103,80,164,0.06)',
              borderColor: '#6750A4',
            },
          }}
        >
          Download Log
        </Button>
      </Box>

      {Object.entries(grouped).map(([tab, entries]) => (
        <div key={tab}>
          <Box sx={{
            fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700,
            color: '#8B7DB5', letterSpacing: '0.06em', textTransform: 'uppercase',
            mb: 1, mt: 2,
          }}>
            {tab} — {entries.length} removed
          </Box>
          <LogPanel>
            {entries.map((entry, i) => (
              <LogEntry key={i}>
                <LogIcon><DeleteIcon /></LogIcon>
                <LogMeta>
                  <LogQuestion>{entry.questionPreview}</LogQuestion>
                  <LogReason>
                    <WarningAmberIcon sx={{ fontSize: 12, color: '#E65100' }} />
                    {entry.reason}
                  </LogReason>
                </LogMeta>
                <LogTimestamp>{formatTime(entry.timestamp)}</LogTimestamp>
              </LogEntry>
            ))}
          </LogPanel>
        </div>
      ))}
    </>
  );
};

// ─── Shared card edit controls ────────────────────────────────────────────────
// Removal is permanent on the server (no restore endpoint), so removed cards
// get no controls — only the "Removed" overlay until the parent refetches.
const CardControls = ({ index, isEditMode, isSelected, isDeleted, onSelect, onDelete }) => {
  if (!isEditMode || isDeleted) return null;
  return (
    <CardEditRow>
      <SelectCheckbox checked={isSelected} onClick={() => onSelect(index)}>
        {isSelected
          ? <CheckBoxIcon />
          : <CheckBoxOutlineBlankIcon />
        }
        {isSelected ? 'Selected' : 'Select'}
      </SelectCheckbox>
      <DeleteBtn onClick={() => onDelete([index])}>
        <DeleteIcon /> Remove
      </DeleteBtn>
    </CardEditRow>
  );
};

// ─── MCQ View ─────────────────────────────────────────────────────────────────
const MCQView = ({ questions, isEditMode, deletedSet, selectedSet, onSelect, onDelete }) => {
  if (!questions || questions.length === 0)
    return <EmptyState><QuizIcon />No MCQ questions generated yet.</EmptyState>;

  return questions.map((q, index) => (
    <QuestionCard key={index} $selected={selectedSet.has(index)} $deleted={deletedSet.has(index)}>
      {deletedSet.has(index) && <DeletedOverlay>Removed</DeletedOverlay>}
      <CardControls
        index={index} isEditMode={isEditMode}
        isSelected={selectedSet.has(index)} isDeleted={deletedSet.has(index)}
        onSelect={onSelect} onDelete={onDelete}
      />
      <QuestionText>
      <QuestionNumber $deleted={deletedSet.has(index)}>{index + 1}</QuestionNumber>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <span style={{
            textDecoration: deletedSet.has(index) ? 'line-through' : 'none',
            opacity: deletedSet.has(index) ? 0.6 : 1,
            flex: 1,
          }}>
            {q.question}
          </span> 
          {!deletedSet.has(index) && q.difficulty && (
            <DifficultyBadge difficulty={q.difficulty}>
              {q.difficulty}
            </DifficultyBadge>
          )}
          {q.added_manually && !deletedSet.has(index) && (  // ✅
            <ManualBadge>
              <EditIcon /> Manual
            </ManualBadge>
          )}
        </Box>
      </Box>
    </QuestionText>
      {!deletedSet.has(index) && q.options && q.options.length > 0 && (
        <OptionsList>
          {q.options.map((opt, idx) => {
            const correct = isCorrectOption(opt, q.correct_answer);
            return (
              <OptionItem key={idx} data-label={optionLabel(idx)} $correct={correct}>
                <span style={{ flex: 1 }}>{cleanText(opt)}</span>
                {correct && <CorrectBadge><CheckCircleIcon /> Correct</CorrectBadge>}
              </OptionItem>
            );
          })}
        </OptionsList>
      )}
      {!deletedSet.has(index) && q.correct_answer && (
        <Box sx={{ mt: '12px', ml: '36px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircleIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 700, color: '#2E7D32' }}>
            Answer: {Array.isArray(q.correct_answer) ? q.correct_answer.join(', ') : q.correct_answer}
          </Typography>
        </Box>
      )}
    </QuestionCard>
  ));
};

// ─── Theory View ──────────────────────────────────────────────────────────────
const SubjectiveView = ({ questions, isEditMode, deletedSet, selectedSet, onSelect, onDelete }) => {
  if (!questions || questions.length === 0)
    return <EmptyState><MenuBookIcon />No theory questions generated yet.</EmptyState>;

  return questions.map((q, index) => {
    const diff = q.difficulty?.toLowerCase();
    const diffStyle = difficultyColors[diff] || { bg: '#F3EEF9', color: '#4F378B' };
    const hasRefAnswer = q.reference_answer && String(q.reference_answer).trim() !== '';
    const hasKeyPoints = q.key_points && (
      Array.isArray(q.key_points) ? q.key_points.length > 0 : String(q.key_points).trim() !== ''
    );
    const keyPointsText = Array.isArray(q.key_points)
      ? q.key_points.map((kp, i) => `${i + 1}. ${kp}`).join('\n')
      : String(q.key_points);

    return (
      <QuestionCard key={index} $selected={selectedSet.has(index)} $deleted={deletedSet.has(index)}>
        {deletedSet.has(index) && <DeletedOverlay>Removed</DeletedOverlay>}
        <CardControls
          index={index} isEditMode={isEditMode}
          isSelected={selectedSet.has(index)} isDeleted={deletedSet.has(index)}
          onSelect={onSelect} onDelete={onDelete}
        />
        <QuestionText>
          <QuestionNumber $deleted={deletedSet.has(index)}>{index + 1}</QuestionNumber>
          <Box sx={{ flex: 1, opacity: deletedSet.has(index) ? 0.5 : 1 }}>
            <span style={{ textDecoration: deletedSet.has(index) ? 'line-through' : 'none' }}>{q.question}</span>
            {!deletedSet.has(index) && (
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '8px', alignItems: 'center' }}>
                {q.skill && <SkillChip label={q.skill} size="small" />}
                {q.difficulty && (
                  <DifficultyChip label={q.difficulty} size="small"
                    sx={{ background: `${diffStyle.bg} !important`, color: `${diffStyle.color} !important` }} />
                )}
              </Box>
            )}
          </Box>
        </QuestionText>
        {!deletedSet.has(index) && hasRefAnswer && (
          <>
            <InfoBoxLabel color="#2e7d32"><CheckCircleIcon /> Reference Answer</InfoBoxLabel>
            <InfoBox bg="#f1faf3" borderColor="#c8e6c9" textColor="#1a3d23">{String(q.reference_answer)}</InfoBox>
          </>
        )}
        {!deletedSet.has(index) && hasKeyPoints && (
          <>
            <InfoBoxLabel color="#1565c0"><FormatListBulletedIcon /> Key Points</InfoBoxLabel>
            <InfoBox bg="#e3f2fd" borderColor="#90caf9" textColor="#0d2d5e">{keyPointsText}</InfoBox>
          </>
        )}
      </QuestionCard>
    );
  });
};

// ─── Coding View ──────────────────────────────────────────────────────────────
const CodingView = ({ questions, isEditMode, deletedSet, selectedSet, onSelect, onDelete }) => {
  if (!questions || questions.length === 0)
    return <EmptyState><CodeIcon />No coding questions generated yet.</EmptyState>;

  return questions.map((q, index) => {
    const category = q.category || 'Coding';
    const catCfg   = categoryConfig[category] || categoryConfig.Coding;
    const diff     = q.difficulty?.toLowerCase();
    const diffStyle = difficultyColors[diff] || { bg: '#F3EEF9', color: '#4F378B' };
    const language = q.programming_language || q.language || '';
    const primaryText = cleanTrailingDashes(
      q.problem_statement || q.scenario || q.question || q.Question || ''
    );
    const isRemoved = deletedSet.has(index);

    return (
      <QuestionCard key={index} $selected={selectedSet.has(index)} $deleted={isRemoved}>
        {isRemoved && <DeletedOverlay>Removed</DeletedOverlay>}
        <CardControls
          index={index} isEditMode={isEditMode}
          isSelected={selectedSet.has(index)} isDeleted={isRemoved}
          onSelect={onSelect} onDelete={onDelete}
        />
        <QuestionText>
          <QuestionNumber $deleted={isRemoved}>{index + 1}</QuestionNumber>
          <Box sx={{ flex: 1, opacity: isRemoved ? 0.5 : 1 }}>
            <span style={{ whiteSpace: 'pre-wrap', textDecoration: isRemoved ? 'line-through' : 'none' }}>
              {primaryText}
            </span>
            {!isRemoved && (
              <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '8px', alignItems: 'center' }}>
                <CategoryBadge bg={catCfg.bg} color={catCfg.color}>{catCfg.icon}&nbsp;{category}</CategoryBadge>
                {q.skill && <SkillChip label={q.skill} size="small" />}
                {q.difficulty && (
                  <DifficultyChip label={q.difficulty} size="small"
                    sx={{ background: `${diffStyle.bg} !important`, color: `${diffStyle.color} !important` }} />
                )}
                {language && <LangBadge style={{ margin: 0 }}><CodeIcon sx={{ fontSize: 11 }} /> {language}</LangBadge>}
              </Box>
            )}
          </Box>
        </QuestionText>

        {!isRemoved && (
        <>
          {q.constraints && (<><InfoBoxLabel color="#4F378B">📐 Constraints</InfoBoxLabel>
            <InfoBox bg="#F3EEF9" borderColor="#D0BCFF" textColor="#2D1B5E">{cleanTrailingDashes(q.constraints)}</InfoBox></>)}

          {q.input_format && (<><InfoBoxLabel color="#1565c0">📥 Input Format</InfoBoxLabel>
            <InfoBox bg="#e3f2fd" borderColor="#90caf9" textColor="#0d2d5e">{cleanTrailingDashes(q.input_format)}</InfoBox></>)}

          {q.output_format && (<><InfoBoxLabel color="#1565c0">📤 Output Format</InfoBoxLabel>
            <InfoBox bg="#e3f2fd" borderColor="#90caf9" textColor="#0d2d5e">{cleanTrailingDashes(q.output_format)}</InfoBox></>)}

          {/* Legacy fallback fields */}
          {q.example_input && (<><InfoBoxLabel color="#1565c0">▶ Example Input</InfoBoxLabel>
            <InfoBox bg="#e3f2fd" borderColor="#90caf9" textColor="#0d2d5e">{cleanTrailingDashes(q.example_input)}</InfoBox></>)}
          {q.expected_output && (<><InfoBoxLabel color="#2e7d32"><CheckCircleIcon /> Expected Output</InfoBoxLabel>
            <InfoBox bg="#f1faf3" borderColor="#c8e6c9" textColor="#1a3d23">{cleanTrailingDashes(stripHtml(q.expected_output))}</InfoBox></>)}

          {/* Examples array (input / output / explanation) */}
          {Array.isArray(q.examples) && q.examples.length > 0 && (
            <>
              <InfoBoxLabel color="#2e7d32"><CheckCircleIcon /> Examples</InfoBoxLabel>
              {q.examples.map((ex, exIdx) => (
                <Box
                  key={exIdx}
                  sx={{
                    margin: '8px 0 12px 36px',
                    border: '1px solid #c8e6c9',
                    borderRadius: `${RADIUS.sm}px`,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    background: '#E8F5E9', color: '#1B5E20',
                    fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700,
                    padding: '4px 16px', letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>
                    Example {exIdx + 1}
                  </Box>
                  <Box sx={{ p: '12px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {ex.input && (
                      <Box>
                        <Box sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#1565C0', mb: '4px' }}>Input</Box>
                        <CodeBlock style={{ margin: 0 }}>{ex.input}</CodeBlock>
                      </Box>
                    )}
                    {ex.output && (
                      <Box>
                        <Box sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#2E7D32', mb: '4px' }}>Output</Box>
                        <CodeBlock style={{ margin: 0 }}>{ex.output}</CodeBlock>
                      </Box>
                    )}
                    {ex.explanation && (
                      <Box>
                        <Box sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#6750A4', mb: '4px' }}>Explanation</Box>
                        <Box sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: '#2D1B5E', lineHeight: 1.5 }}>{ex.explanation}</Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </>
          )}

    {/* Conceptual-style field */}
    {q.expected_discussion_points && (<><InfoBoxLabel color="#6A1B9A"><LightbulbIcon /> Expected Discussion Points</InfoBoxLabel>
      <InfoBox bg="#F3E5F5" borderColor="#CE93D8" textColor="#4A148C">{cleanTrailingDashes(q.expected_discussion_points)}</InfoBox></>)}

    {/* Debugging-style fields */}
    {q.code_scenario && (<><InfoBoxLabel color="#B71C1C"><BugReportIcon /> Code / Scenario</InfoBoxLabel>
      <CodeBlock>{stripCodeFences(q.code_scenario)}</CodeBlock></>)}
    {q.observed_behavior && (<><InfoBoxLabel color="#c62828">⚠ Observed Behavior</InfoBoxLabel>
      <InfoBox bg="#fff5f5" borderColor="#ffcdd2" textColor="#7f0000">{cleanTrailingDashes(q.observed_behavior)}</InfoBox></>)}
    {category === 'Debugging' && q.question && (<><InfoBoxLabel color="#B71C1C">❓ Question</InfoBoxLabel>
      <InfoBox bg="#FFEBEE" borderColor="#EF9A9A" textColor="#7f0000">{cleanTrailingDashes(stripHtml(q.question))}</InfoBox></>)}

    {/* Tooling-style fields */}
    {q.task && (<><InfoBoxLabel color="#1B5E20"><BuildIcon /> Task</InfoBoxLabel>
      <InfoBox bg="#E8F5E9" borderColor="#A5D6A7" textColor="#1B5E20">{cleanTrailingDashes(q.task)}</InfoBox></>)}
    {q.expected_approach && (<><InfoBoxLabel color="#2e7d32"><CheckCircleIcon /> Expected Approach</InfoBoxLabel>
      <InfoBox bg="#f1faf3" borderColor="#c8e6c9" textColor="#1a3d23">{cleanTrailingDashes(q.expected_approach)}</InfoBox></>)}
  </>
)}
      </QuestionCard>
    );
  });
};

// ─── PDF Generator (unchanged) ────────────────────────────────────────────────
const generatePdf = (mcqQuestions, subjectiveQuestions, codingQuestions) => {
  const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
  const PAGE_W = 595, PAGE_H = 842, MARGIN = 40, CONTENT_W = PAGE_W - MARGIN * 2;
  let y = MARGIN;

  const checkNewPage = (h = 20) => {
    if (y + h > PAGE_H - MARGIN) { doc.addPage(); y = MARGIN; }
  };
  const printText = (text, x, fontSize, color, bold, maxWidth) => {
    doc.setFontSize(fontSize); doc.setTextColor(...color);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(String(text || ''), maxWidth);
    lines.forEach(line => { checkNewPage(fontSize + 4); doc.text(line, x, y); y += fontSize + 4; });
  };
  const printBox = (text, bgColor, borderColor, fontSize = 10) => {
    doc.setFontSize(fontSize); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
    const lines = doc.splitTextToSize(String(cleanTrailingDashes(text) || ''), CONTENT_W - 16);
    const boxH = lines.length * (fontSize + 4) + 12;
    checkNewPage(boxH + 8);
    doc.setFillColor(...bgColor); doc.setDrawColor(...borderColor);
    doc.roundedRect(MARGIN, y, CONTENT_W, boxH, 4, 4, 'FD');
    let ty = y + 8;
    lines.forEach(line => { doc.text(line, MARGIN + 8, ty); ty += fontSize + 4; });
    y += boxH + 6;
  };
  const printCodeBox = (text, fontSize = 9) => {
    const clean = stripCodeFences(text);
    doc.setFontSize(fontSize); doc.setFont('courier', 'normal'); doc.setTextColor(30, 30, 50);
    const lines = doc.splitTextToSize(String(clean || ''), CONTENT_W - 16);
    const boxH = lines.length * (fontSize + 3) + 12;
    checkNewPage(boxH + 8);
    doc.setFillColor(30, 30, 46); doc.setDrawColor(49, 50, 68);
    doc.roundedRect(MARGIN, y, CONTENT_W, boxH, 4, 4, 'FD');
    doc.setTextColor(205, 214, 244);
    let ty = y + 8;
    lines.forEach(line => { doc.text(line, MARGIN + 8, ty); ty += fontSize + 3; });
    y += boxH + 6;
  };
  const printLabel = (text, color) => printText(text, MARGIN, 10, color, true, CONTENT_W);
  const printDivider = () => {
    checkNewPage(12); doc.setDrawColor(210, 200, 230); doc.setLineWidth(0.5);
    doc.line(MARGIN, y, PAGE_W - MARGIN, y); y += 10;
  };
  const printSectionTitle = (title) => {
    checkNewPage(30); y += 6;
    doc.setFillColor(79, 55, 139); doc.rect(MARGIN, y - 14, CONTENT_W, 22, 'F');
    doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
    doc.text(title, MARGIN + 8, y); y += 16;
  };

  doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(79, 55, 139);
  doc.text('Question Bank', MARGIN, y); y += 10;
  doc.setFontSize(10); doc.setFont('helvetica', 'normal'); doc.setTextColor(139, 125, 181);
  const total = (mcqQuestions?.length || 0) + (subjectiveQuestions?.length || 0) + (codingQuestions?.length || 0);
  doc.text(`${total} questions across MCQ, Theory, and Coding`, MARGIN, y + 6); y += 22;
  printDivider();

  printSectionTitle(`MCQ Questions  (${mcqQuestions?.length || 0})`); y += 6;
  if (mcqQuestions?.length > 0) {
    mcqQuestions.forEach((q, i) => {
      checkNewPage(40);
      let qLine = `${i + 1}. ${q.question}`;

      if (q.difficulty) {
        qLine += `  [${q.difficulty}]`;
      }

      printText(qLine, MARGIN, 11, [30, 30, 30], true, CONTENT_W);
      (q.options || []).forEach((opt, idx) => {
        printText(`   ${optionLabel(idx)}. ${cleanText(opt)}`, MARGIN + 8, 10, [60, 60, 60], false, CONTENT_W - 8);
      });
      y += 2;
      if (q.correct_answer) {
        const ans = Array.isArray(q.correct_answer) ? q.correct_answer.join(', ') : q.correct_answer;
        printText(`Answer: ${ans}`, MARGIN, 10, [0, 120, 0], true, CONTENT_W);
      }
      y += 4;
      if (i !== mcqQuestions.length - 1) printDivider();
    });
  } else printText('No MCQ questions available.', MARGIN, 10, [150, 150, 150], false, CONTENT_W);

  y += 12; printSectionTitle(`Theory Questions  (${subjectiveQuestions?.length || 0})`); y += 6;
  if (subjectiveQuestions?.length > 0) {
    subjectiveQuestions.forEach((q, i) => {
      checkNewPage(40);
      let qLine = `${i + 1}. ${q.question}`;
      if (q.skill) qLine += `  [${q.skill}]`;
      if (q.difficulty) qLine += `  [${q.difficulty}]`;
      printText(qLine, MARGIN, 11, [30, 30, 30], true, CONTENT_W); y += 2;
      if (q.reference_answer?.trim()) { printLabel('Reference Answer:', [46, 125, 50]); printBox(q.reference_answer, [241, 250, 243], [200, 230, 201]); }
      if (q.key_points) {
        const kpText = Array.isArray(q.key_points) ? q.key_points.map((kp, ki) => `${ki + 1}. ${kp}`).join('\n') : String(q.key_points);
        if (kpText.trim()) { printLabel('Key Points:', [21, 101, 192]); printBox(kpText, [227, 242, 253], [144, 202, 249]); }
      }
      y += 4;
      if (i !== subjectiveQuestions.length - 1) printDivider();
    });
  } else printText('No theory questions available.', MARGIN, 10, [150, 150, 150], false, CONTENT_W);

  y += 12; printSectionTitle(`Coding Questions  (${codingQuestions?.length || 0})`); y += 6;
  if (codingQuestions?.length > 0) {
    codingQuestions.forEach((q, i) => {
      checkNewPage(40);
      const category = q.category || 'Coding';
      const language = q.programming_language || q.language || '';
      printText(`[${category}]${language ? '  |  ' + language : ''}${q.skill ? '  |  ' + q.skill : ''}${q.difficulty ? '  |  ' + q.difficulty : ''}`, MARGIN, 9, [103, 80, 164], true, CONTENT_W); y += 2;
      const primaryText = q.problem_statement || q.scenario || q.question || q.Question || '';
      printText(`${i + 1}. ${primaryText}`, MARGIN, 11, [30, 30, 30], true, CONTENT_W); y += 2;
      if (q.constraints) { printLabel('Constraints:', [79, 55, 139]); printBox(q.constraints, [243, 238, 249], [208, 188, 255]); }
      if (q.input_format)  { printLabel('Input Format:', [21, 101, 192]); printBox(q.input_format, [227, 242, 253], [144, 202, 249]); }
      if (q.output_format) { printLabel('Output Format:', [21, 101, 192]); printBox(q.output_format, [227, 242, 253], [144, 202, 249]); }

      // Legacy fallback
      if (q.example_input) { printLabel('Example Input:', [21, 101, 192]); printBox(q.example_input, [227, 242, 253], [144, 202, 249]); }
      if (q.expected_output) { printLabel('Expected Output:', [46, 125, 50]); printBox(stripHtml(q.expected_output), [241, 250, 243], [200, 230, 201]); }

      // Examples array
      if (Array.isArray(q.examples) && q.examples.length > 0) {
      q.examples.forEach((ex, exIdx) => {
        printLabel(`Example ${exIdx + 1}:`, [46, 125, 50]);
        if (ex.input) { printLabel('  Input:', [21, 101, 192]); printCodeBox(ex.input); }
        if (ex.output) { printLabel('  Output:', [46, 125, 50]); printCodeBox(ex.output); }
        if (ex.explanation) { printLabel('  Explanation:', [103, 80, 164]); printBox(ex.explanation, [243, 238, 249], [208, 188, 255]); }
      });
      }

if (q.expected_discussion_points) { printLabel('Expected Discussion Points:', [106, 27, 154]); printBox(q.expected_discussion_points, [243, 229, 245], [206, 147, 216]); }

if (q.code_scenario) { printLabel('Code / Scenario:', [183, 28, 28]); printCodeBox(q.code_scenario); }
if (q.observed_behavior) { printLabel('Observed Behavior:', [183, 28, 28]); printBox(q.observed_behavior, [255, 235, 238], [239, 154, 154]); }
if (category === 'Debugging' && q.question) { printLabel('Question:', [183, 28, 28]); printBox(stripHtml(q.question), [255, 235, 238], [239, 154, 154]); }

if (q.task) { printLabel('Task:', [27, 94, 32]); printBox(q.task, [232, 245, 233], [165, 214, 167]); }
if (q.expected_approach) { printLabel('Expected Approach:', [46, 125, 50]); printBox(q.expected_approach, [241, 250, 243], [200, 230, 201]); }
      y += 6;
      if (i !== codingQuestions.length - 1) printDivider();
    });
  } else printText('No coding questions available.', MARGIN, 10, [150, 150, 150], false, CONTENT_W);

  doc.save('question_bank.pdf');
};

// ─── Regenerate Prompt Component ──────────────────────────────────────────────
const RegeneratePrompt = ({ removedCounts, onConfirm, onCancel, loading }) => {
  const [counts, setCounts] = useState({
    mcq:        removedCounts.mcq        || 0,
    subjective: removedCounts.subjective || 0,
    coding:     removedCounts.coding     || 0,
  });

  const [skillInput,    setSkillInput]    = useState('');
  const [skills,        setSkills]        = useState([]);
  const [secSkillInput, setSecSkillInput] = useState('');
  const [secSkills,     setSecSkills]     = useState([]);
  const [language,      setLanguage]      = useState('');
  const [experience,    setExperience]    = useState('');

  const total = counts.mcq + counts.subjective + counts.coding;

  const addSkill = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && skillInput.trim()) {
      e.preventDefault();
      const val = skillInput.trim().replace(/,$/, '');
      if (val && !skills.includes(val)) setSkills(p => [...p, val]);
      setSkillInput('');
    }
  };

  const addSecSkill = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && secSkillInput.trim()) {
      e.preventDefault();
      const val = secSkillInput.trim().replace(/,$/, '');
      if (val && !secSkills.includes(val)) setSecSkills(p => [...p, val]);
      setSecSkillInput('');
    }
  };

  const CountStepper = ({ label, icon, field, max }) => (
    <RegenerateRow>
      <RegenerateLabel>{icon} {label}</RegenerateLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <button
          onClick={() => setCounts(p => ({ ...p, [field]: Math.max(0, p[field] - 1) }))}
          style={{ border: '1.5px solid #D0BCFF', borderRadius: RADIUS.sm, width: 32, height: 32,
            background: '#F3EEF9', cursor: 'pointer', fontWeight: 700, color: '#6750A4' }}>−</button>
        <CountInput
          type="number" min={0} max={max}
          value={counts[field]}
          onChange={e => setCounts(p => ({ ...p, [field]: Math.max(0, parseInt(e.target.value) || 0) }))}
        />
        <button
          onClick={() => setCounts(p => ({ ...p, [field]: Math.min(max, p[field] + 1) }))}
          style={{ border: '1.5px solid #D0BCFF', borderRadius: RADIUS.sm, width: 32, height: 32,
            background: '#F3EEF9', cursor: 'pointer', fontWeight: 700, color: '#6750A4' }}>+</button>
      </Box>
    </RegenerateRow>
  );

  return (
    <Dialog
    open
    onClose={() => { if (!loading) onCancel(); }}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: `${RADIUS.lg}px`,
        boxShadow: '0 24px 60px rgba(79,55,139,0.25)',
        maxHeight: '88vh',
      },
    }}
  >
    <RegenerateCard
      style={{ maxHeight: '88vh', overflowY: 'auto' }}
      onClick={e => e.stopPropagation()}
    >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutorenewIcon sx={{ color: '#6750A4', fontSize: 20 }} />
          <ReasonTitle>Regenerate & Append Questions</ReasonTitle>
        </Box>
        <ReasonSubtitle>
          Specify skills, language, experience, and how many questions to generate per section.
        </ReasonSubtitle>

        {/* ── Question Counts ── */}
        <Box sx={{
          fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700,
          color: '#8B7DB5', letterSpacing: '0.06em', textTransform: 'uppercase',
          mb: 1,
        }}>
          Question Counts
        </Box>
        <Box sx={{ border: '1px solid #EDE7F6', borderRadius: `${RADIUS.md}px`, px: 2, py: 1, mb: 2 }}>
          <CountStepper label="MCQ"    icon={<QuizIcon sx={{ fontSize: 16, color: '#6750A4' }} />}    field="mcq"        max={50} />
          <CountStepper label="Theory" icon={<MenuBookIcon sx={{ fontSize: 16, color: '#6750A4' }} />} field="subjective" max={30} />
          <CountStepper label="Coding" icon={<CodeIcon sx={{ fontSize: 16, color: '#6750A4' }} />}    field="coding"     max={20} />
        </Box>

        {/* Auto-fill hint */}
        {(removedCounts.mcq > 0 || removedCounts.subjective > 0 || removedCounts.coding > 0) && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              onClick={() => setCounts({ ...removedCounts })}
              sx={{
                fontFamily: FONT_BODY, fontSize: TYPE.caption, fontWeight: 700,
                color: '#6750A4', textTransform: 'none', border: '1.5px solid #D0BCFF',
                borderRadius: `${RADIUS.sm}px`, px: 1.5, minHeight: 32,
              }}
            >
              Auto-fill removed counts ({removedCounts.mcq} MCQ · {removedCounts.subjective} Theory · {removedCounts.coding} Coding)
            </Button>
          </Box>
        )}

        {/* ── Context Fields ── */}
        <Box sx={{
          fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700,
          color: '#8B7DB5', letterSpacing: '0.06em', textTransform: 'uppercase',
          mb: 1,
        }}>
          Generation Context
        </Box>
        <Box sx={{ border: '1px solid #EDE7F6', borderRadius: `${RADIUS.md}px`, p: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Primary Skills */}
          <FormSection style={{ margin: 0 }}>
            <FormLabel>
              <LightbulbIcon sx={{ fontSize: 13 }} /> Primary Skills
              <span style={{ color: '#B0A0D0', fontWeight: 500, textTransform: 'none', fontSize: 11 }}>
                &nbsp;(press Enter or comma to add)
              </span>
            </FormLabel>
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', gap: '4px',
              border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`, alignItems: 'center',
              padding: '4px 12px', minHeight: 40, boxSizing: 'border-box',
              '&:focus-within': { borderColor: '#6750A4' },
            }}>
              {skills.map(s => (
                <Box key={s} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  background: 'linear-gradient(135deg, #4F378B, #6750A4)',
                  color: '#fff', borderRadius: `${RADIUS.pill}px`, padding: '2px 8px',
                  fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
                }}>
                  {s}
                  <span
                    onClick={() => setSkills(p => p.filter(x => x !== s))}
                    style={{ cursor: 'pointer', opacity: 0.8, marginLeft: 2, fontSize: 14, lineHeight: 1 }}
                  >×</span>
                </Box>
              ))}
              <input
                value={skillInput}
                onChange={e => setSkillInput(e.target.value)}
                onKeyDown={addSkill}
                onBlur={() => {
                  const val = skillInput.trim();
                  if (val && !skills.includes(val)) setSkills(p => [...p, val]);
                  setSkillInput('');
                }}
                placeholder={skills.length === 0 ? 'e.g. React, Node.js, SQL…' : ''}
                style={{
                  border: 'none', outline: 'none', fontFamily: FONT_BODY,
                  fontSize: TYPE.body, color: '#2D1B5E', background: 'transparent',
                  flex: 1, minWidth: 120,
                }}
              />
            </Box>
          </FormSection>

          {/* Secondary Skills */}
          <FormSection style={{ margin: 0 }}>
            <FormLabel>
              <BuildIcon sx={{ fontSize: 13 }} /> Secondary Skills
              <span style={{ color: '#B0A0D0', fontWeight: 500, textTransform: 'none', fontSize: 11 }}>
                &nbsp;(optional)
              </span>
            </FormLabel>
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', gap: '4px',
              border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`, alignItems: 'center',
              padding: '4px 12px', minHeight: 40, boxSizing: 'border-box',
              '&:focus-within': { borderColor: '#6750A4' },
            }}>
              {secSkills.map(s => (
                <Box key={s} sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  background: '#EDE7F6', color: '#4F378B',
                  borderRadius: `${RADIUS.pill}px`, padding: '2px 8px',
                  fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
                }}>
                  {s}
                  <span
                    onClick={() => setSecSkills(p => p.filter(x => x !== s))}
                    style={{ cursor: 'pointer', opacity: 0.7, marginLeft: 2, fontSize: 14, lineHeight: 1 }}
                  >×</span>
                </Box>
              ))}
              <input
                value={secSkillInput}
                onChange={e => setSecSkillInput(e.target.value)}
                onKeyDown={addSecSkill}
                onBlur={() => {
                  const val = secSkillInput.trim();
                  if (val && !secSkills.includes(val)) setSecSkills(p => [...p, val]);
                  setSecSkillInput('');
                }}
                placeholder={secSkills.length === 0 ? 'e.g. Docker, Git, Jest…' : ''}
                style={{
                  border: 'none', outline: 'none', fontFamily: FONT_BODY,
                  fontSize: TYPE.body, color: '#2D1B5E', background: 'transparent',
                  flex: 1, minWidth: 120,
                }}
              />
            </Box>
          </FormSection>

          {/* Language + Experience side by side */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormSection style={{ margin: 0, flex: 1 }}>
              <FormLabel><CodeIcon sx={{ fontSize: 13 }} /> Programming Language</FormLabel>
              <StyledInput
                placeholder="e.g. Python, Java, TypeScript…"
                value={language}
                onChange={e => setLanguage(e.target.value)}
              />
            </FormSection>
            <FormSection style={{ margin: 0, flex: 1 }}>
              <FormLabel>Experience (years)</FormLabel>
              <StyledInput
                type="number"
                min={0}
                max={30}
                placeholder="e.g. 3"
                value={experience}
                onChange={e => setExperience(e.target.value)}
              />
            </FormSection>
          </Box>

        </Box>

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={loading} sx={{
            fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
            color: '#8B7DB5', border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`,
            textTransform: 'none', px: 2,
          }}>
            Cancel
          </Button>
          <Button
            disabled={total === 0 || loading}
            onClick={() => onConfirm({ counts, skills, secSkills, language, experience })}
            sx={{
              background: total > 0 && !loading ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#EEE',
              color: total > 0 && !loading ? '#fff' : '#aaa',
              fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
              borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
              boxShadow: total > 0 ? '0 4px 14px rgba(79,55,139,0.3)' : 'none',
            }}
          >
            {loading
              ? <><CircularProgress size={16} sx={{ color: '#aaa', mr: 1 }} /> Generating…</>
              : <><AutorenewIcon sx={{ fontSize: 16, mr: 0.5 }} /> Generate {total > 0 ? `+${total}` : ''} Questions</>
            }
          </Button>
        </Box>
        </RegenerateCard>
        </Dialog>
  );
};

const DIFFICULTIES = ['easy', 'medium', 'hard'];
const CODING_CATEGORIES = ['Coding', 'Conceptual', 'Debugging', 'Tooling'];

const defaultMcq = () => ({
  question: '',
  options: ['', '', '', ''],
  correct_answer: [],
});

const defaultSubjective = () => ({
  question: '',
  sub_topic: '',
  question_angle: '',
  reference_answer: '',
  key_points: '',
  skill: '',
  difficulty: 'medium',
});

const defaultCoding = () => ({
  category: 'Coding',
  difficulty: 'medium',
  skill: '',
  problem_theme: '',
  language: '',
  programming_language: '',
  problem_statement: '',
  constraints: '',
  input_format: '',
  output_format: '',
  examples: [{ input: '', output: '', explanation: '' }],
  // Legacy fallback (kept for backward compatibility, not shown in form)
  example_input: '',
  expected_output: '',
  expected_discussion_points: '',
  code_scenario: '',
  observed_behavior: '',
  question: '',
  scenario: '',
  task: '',
  expected_approach: '',
});

const AddManualPrompt = ({ activeTab, onConfirm, onCancel, saving }) => {
  const [type, setType]   = useState(activeTab !== 'log' ? activeTab : 'mcq');
  const [mcq,  setMcq]    = useState(defaultMcq());
  const [subj, setSubj]   = useState(defaultSubjective());
  const [code, setCode]   = useState(defaultCoding());
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Any field differing from its default counts as unsaved input
  const isDirty = () =>
    JSON.stringify(mcq)  !== JSON.stringify(defaultMcq()) ||
    JSON.stringify(subj) !== JSON.stringify(defaultSubjective()) ||
    JSON.stringify(code) !== JSON.stringify(defaultCoding());

  // Backdrop / Esc / Cancel / X: ignored while saving, confirm before discarding input
  const requestCancel = () => {
    if (saving) return;
    if (isDirty()) setConfirmDiscard(true);
    else onCancel();
  };

  // ── MCQ helpers ────────────────────────────────────────────────────────────
  // correct_answer stores option TEXT, so editing an option that is marked
  // correct must carry the mark over to the new text (or drop it if emptied).
  const setOption = (i, val) =>
    setMcq(p => {
      const o = [...p.options];
      const prev = o[i];
      o[i] = val;
      const stillUsed = o.some((opt, idx) => idx !== i && opt === prev);
      let correct = p.correct_answer;
      if (p.correct_answer.includes(prev) && !stillUsed) {
        correct = p.correct_answer.filter(a => a !== prev);
        if (val.trim() && !correct.includes(val)) correct = [...correct, val];
      }
      return { ...p, options: o, correct_answer: correct };
    });

  const toggleCorrect = (opt) =>
    setMcq(p => ({
      ...p,
      correct_answer: p.correct_answer.includes(opt)
        ? p.correct_answer.filter(a => a !== opt)
        : [...p.correct_answer, opt],
    }));

  const addOption = () =>
    setMcq(p => ({ ...p, options: [...p.options, ''] }));

  const removeOption = (i) =>
    setMcq(p => ({
      options: p.options.filter((_, idx) => idx !== i),
      correct_answer: p.correct_answer.filter(a => a !== p.options[i]),
      question: p.question,
    }));

  // ── Validation ─────────────────────────────────────────────────────────────
  const isValid = () => {
    if (type === 'mcq') {
      const filled = mcq.options.filter(o => o.trim());
      return !!mcq.question.trim() &&
             filled.length >= 2 &&
             mcq.correct_answer.length > 0 &&
             mcq.correct_answer.every(a => filled.includes(a));
    }
    if (type === 'subjective') return subj.question.trim().length > 0;
    if (type === 'coding') {
      const primary = code.problem_statement || code.scenario || code.question || code.task;
      if (!primary.trim()) return false;
      if (code.category === 'Coding') {
        const hasValidExample = code.examples.some(ex => ex.input.trim() || ex.output.trim());
        // Not strictly required — comment out the next line if examples should stay optional
        // if (!hasValidExample) return false;
      }
      return true;
    }
    return false;
  };

  const handleConfirm = () => {
  if (saving || !isValid()) return;
  if (type === 'mcq') {
    onConfirm(type, {
      ...mcq,
      options: mcq.options.filter(o => o.trim()),
    });
  } else if (type === 'subjective') {
    onConfirm(type, subj);
  } else {
    onConfirm(type, {
      ...code,
      programming_language: code.language || code.programming_language,
      examples: code.examples.filter(ex => ex.input.trim() || ex.output.trim()),
    });
  }
};

  return (
    <>
    <Dialog
      open
      onClose={requestCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${RADIUS.lg}px`,
          boxShadow: '0 24px 60px rgba(79,55,139,0.25)',
          overflow: 'visible',
        },
      }}
    >
      <AddManualCard onClick={e => e.stopPropagation()}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <ReasonTitle>Add Question Manually</ReasonTitle>
            <ReasonSubtitle style={{ margin: 0 }}>
              Question will be added to the JD bank immediately.
            </ReasonSubtitle>
          </Box>
          <CloseBtn onClick={requestCancel} disabled={saving} size="small"><CloseIcon /></CloseBtn>
        </Box>

        {/* Type selector */}
        <TypeTabBar>
          {[
            { key: 'mcq',        label: 'MCQ',    icon: <QuizIcon /> },
            { key: 'subjective', label: 'Theory', icon: <MenuBookIcon /> },
            { key: 'coding',     label: 'Coding', icon: <CodeIcon /> },
          ].map(t => (
            <TypeTab key={t.key} active={type === t.key} onClick={() => setType(t.key)}>
              {t.icon} {t.label}
            </TypeTab>
          ))}
        </TypeTabBar>

        {/* ── MCQ Form ── */}
        {type === 'mcq' && (
          <>
            <FormSection>
              <FormLabel><QuizIcon sx={{ fontSize: 13 }} /> Question *</FormLabel>
              <StyledTextarea
                rows={3}
                placeholder="Enter your MCQ question..."
                value={mcq.question}
                onChange={e => setMcq(p => ({ ...p, question: e.target.value }))}
              />
            </FormSection>

            <FormSection>
              <FormLabel>Options * <span style={{ color: '#B0A0D0', fontWeight: 500, textTransform: 'none', fontSize: 11 }}>(toggle correct answers)</span></FormLabel>
              {mcq.options.map((opt, i) => (
                <OptionRow key={i}>
                  <Box sx={{
                    width: 24, height: 24, borderRadius: '6px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    background: 'linear-gradient(135deg, #4F378B, #6750A4)',
                    color: '#fff', fontSize: TYPE.overline, fontWeight: 700, fontFamily: FONT_BODY,
                  }}>
                    {optionLabel(i)}
                  </Box>
                  <StyledInput
                    placeholder={`Option ${optionLabel(i)}`}
                    value={opt}
                    onChange={e => setOption(i, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <CorrectToggle
                    correct={mcq.correct_answer.includes(opt) && opt.trim()}
                    onClick={() => opt.trim() && toggleCorrect(opt)}
                  >
                    <CheckCircleIcon />
                    {mcq.correct_answer.includes(opt) && opt.trim() ? 'Correct' : 'Mark'}
                  </CorrectToggle>
                  {mcq.options.length > 2 && (
                    <IconButton size="small" onClick={() => removeOption(i)}
                      sx={{ color: '#EF5350' }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </OptionRow>
              ))}
              {mcq.options.length < 6 && (
                <AddOptionBtn onClick={addOption}>+ Add Option</AddOptionBtn>
              )}
            </FormSection>
          </>
        )}

        {/* ── Subjective Form ── */}
        {type === 'subjective' && (
          <>
            <FormSection>
              <FormLabel><MenuBookIcon sx={{ fontSize: 13 }} /> Question *</FormLabel>
              <StyledTextarea rows={3} placeholder="Enter your theory question..."
                value={subj.question}
                onChange={e => setSubj(p => ({ ...p, question: e.target.value }))} />
            </FormSection>
            <FormSection>
              <FormLabel><CheckCircleIcon sx={{ fontSize: 13 }} /> Reference Answer</FormLabel>
              <StyledTextarea rows={3} placeholder="Ideal answer for evaluation..."
                value={subj.reference_answer}
                onChange={e => setSubj(p => ({ ...p, reference_answer: e.target.value }))} />
            </FormSection>
            <FormSection>
              <FormLabel><FormatListBulletedIcon sx={{ fontSize: 13 }} /> Key Points</FormLabel>
              <StyledTextarea rows={2} placeholder="Key points to look for, one per line..."
                value={subj.key_points}
                onChange={e => setSubj(p => ({ ...p, key_points: e.target.value }))} />
            </FormSection>
            <Box sx={{ display: 'flex', gap: 2 }}>
  <FormSection style={{ flex: 1 }}>
    <FormLabel>Skill</FormLabel>
    <StyledInput placeholder="e.g. C/C++, React..."
      value={subj.skill}
      onChange={e => setSubj(p => ({ ...p, skill: e.target.value }))} />
  </FormSection>
  <FormSection style={{ flex: 1 }}>
    <FormLabel>Sub Topic</FormLabel>
    <StyledInput placeholder="e.g. pointer arithmetic and type casting..."
      value={subj.sub_topic}
      onChange={e => setSubj(p => ({ ...p, sub_topic: e.target.value }))} />
  </FormSection>
</Box>
<Box sx={{ display: 'flex', gap: 2 }}>
  <FormSection style={{ flex: 1 }}>
    <FormLabel>Question Angle</FormLabel>
    <StyledSelect value={subj.question_angle}
      onChange={e => setSubj(p => ({ ...p, question_angle: e.target.value }))}>
      <option value="">Select angle…</option>
      <option value="debug/troubleshoot">debug/troubleshoot</option>
      <option value="explain how/why">explain how/why</option>
      <option value="compare A vs B">compare A vs B</option>
      <option value="what happens when">what happens when</option>
      <option value="design/architect">design/architect</option>
    </StyledSelect>
  </FormSection>
  <FormSection style={{ flex: 1 }}>
    <FormLabel>Difficulty</FormLabel>
    <StyledSelect value={subj.difficulty}
      onChange={e => setSubj(p => ({ ...p, difficulty: e.target.value }))}>
      {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
    </StyledSelect>
  </FormSection>
</Box>
          </>
        )}

        {/* ── Coding Form ── */}
        {type === 'coding' && (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 0 }}>
              <FormSection style={{ flex: 1 }}>
                <FormLabel>Category *</FormLabel>
                <StyledSelect value={code.category}
                  onChange={e => setCode(p => ({ ...p, category: e.target.value }))}>
                  {CODING_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </StyledSelect>
              </FormSection>
              <FormSection style={{ flex: 1 }}>
                <FormLabel>Difficulty</FormLabel>
                <StyledSelect value={code.difficulty}
                  onChange={e => setCode(p => ({ ...p, difficulty: e.target.value }))}>
                  {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                </StyledSelect>
              </FormSection>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 0 }}>
              <FormSection style={{ flex: 1 }}>
                <FormLabel>Skill</FormLabel>
                <StyledInput placeholder="e.g. Arrays, SQL..."
                  value={code.skill}
                  onChange={e => setCode(p => ({ ...p, skill: e.target.value }))} />
              </FormSection>
              <FormSection style={{ flex: 1 }}>
                <FormLabel>Language</FormLabel>
                <StyledInput placeholder="e.g. Python, Java..."
                  value={code.language}
                  onChange={e => setCode(p => ({ ...p, language: e.target.value, programming_language: e.target.value }))} />
              </FormSection>
            </Box>

            {/* Fields vary by category */}
            {code.category === 'Coding' && (<>
  <FormSection>
    <FormLabel>Problem Theme</FormLabel>
    <StyledInput placeholder="e.g. pointer arithmetic and type casting..."
      value={code.problem_theme}
      onChange={e => setCode(p => ({ ...p, problem_theme: e.target.value }))} />
  </FormSection>
  <FormSection>
    <FormLabel><CodeIcon sx={{ fontSize: 13 }} /> Problem Statement *</FormLabel>
    <StyledTextarea rows={4} placeholder="Describe the coding problem..."
      value={code.problem_statement}
      onChange={e => setCode(p => ({ ...p, problem_statement: e.target.value }))} />
  </FormSection>
  <Box sx={{ display: 'flex', gap: 2 }}>
    <FormSection style={{ flex: 1 }}>
      <FormLabel>Input Format</FormLabel>
      <StyledTextarea rows={2} placeholder="Describe the input format..."
        value={code.input_format}
        onChange={e => setCode(p => ({ ...p, input_format: e.target.value }))} />
    </FormSection>
    <FormSection style={{ flex: 1 }}>
      <FormLabel>Output Format</FormLabel>
      <StyledTextarea rows={2} placeholder="Describe the output format..."
        value={code.output_format}
        onChange={e => setCode(p => ({ ...p, output_format: e.target.value }))} />
    </FormSection>
  </Box>
  <FormSection>
    <FormLabel>Constraints</FormLabel>
    <StyledTextarea rows={2} placeholder="e.g. 1 ≤ n ≤ 10^5"
      value={code.constraints}
      onChange={e => setCode(p => ({ ...p, constraints: e.target.value }))} />
  </FormSection>

  {/* Examples array */}
  <FormSection>
    <FormLabel>Examples</FormLabel>
    {code.examples.map((ex, i) => (
      <Box key={i} sx={{
        border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`, p: 1.5, mb: 1.5,
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700, color: '#6750A4' }}>
            Example {i + 1}
          </Box>
          {code.examples.length > 1 && (
            <IconButton size="small"
              onClick={() => setCode(p => ({ ...p, examples: p.examples.filter((_, idx) => idx !== i) }))}
              sx={{ color: '#EF5350' }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        <StyledTextarea rows={2} placeholder="Input..."
          value={ex.input}
          onChange={e => setCode(p => {
            const examples = [...p.examples];
            examples[i] = { ...examples[i], input: e.target.value };
            return { ...p, examples };
          })} />
        <StyledTextarea rows={2} placeholder="Output..."
          value={ex.output}
          onChange={e => setCode(p => {
            const examples = [...p.examples];
            examples[i] = { ...examples[i], output: e.target.value };
            return { ...p, examples };
          })} />
        <StyledTextarea rows={2} placeholder="Explanation (optional)..."
          value={ex.explanation}
          onChange={e => setCode(p => {
            const examples = [...p.examples];
            examples[i] = { ...examples[i], explanation: e.target.value };
            return { ...p, examples };
          })} />
      </Box>
    ))}
    <AddOptionBtn
      onClick={() => setCode(p => ({ ...p, examples: [...p.examples, { input: '', output: '', explanation: '' }] }))}
    >
      + Add Example
    </AddOptionBtn>
  </FormSection>
</>)}

            {code.category === 'Conceptual' && (<>
              <FormSection>
                <FormLabel><LightbulbIcon sx={{ fontSize: 13 }} /> Question *</FormLabel>
                <StyledTextarea rows={3} placeholder="Conceptual question..."
                  value={code.question}
                  onChange={e => setCode(p => ({ ...p, question: e.target.value }))} />
              </FormSection>
              <FormSection>
                <FormLabel>Expected Discussion Points</FormLabel>
                <StyledTextarea rows={3} placeholder="What should the candidate discuss..."
                  value={code.expected_discussion_points}
                  onChange={e => setCode(p => ({ ...p, expected_discussion_points: e.target.value }))} />
              </FormSection>
            </>)}

            {code.category === 'Debugging' && (<>
              <FormSection>
                <FormLabel><BugReportIcon sx={{ fontSize: 13 }} /> Code / Scenario *</FormLabel>
                <StyledTextarea rows={5} placeholder="Paste the buggy code here..."
                  value={code.code_scenario}
                  onChange={e => setCode(p => ({ ...p, code_scenario: e.target.value }))} />
              </FormSection>
              <FormSection>
                <FormLabel>Observed Behavior</FormLabel>
                <StyledTextarea rows={2} placeholder="What does the broken code do..."
                  value={code.observed_behavior}
                  onChange={e => setCode(p => ({ ...p, observed_behavior: e.target.value }))} />
              </FormSection>
              <FormSection>
                <FormLabel>Question *</FormLabel>
                <StyledTextarea rows={2} placeholder="What should the candidate find/fix..."
                  value={code.question}
                  onChange={e => setCode(p => ({ ...p, question: e.target.value }))} />
              </FormSection>
            </>)}

            {code.category === 'Tooling' && (<>
              <FormSection>
                <FormLabel>Scenario *</FormLabel>
                <StyledTextarea rows={3} placeholder="Describe the tooling scenario..."
                  value={code.scenario}
                  onChange={e => setCode(p => ({ ...p, scenario: e.target.value }))} />
              </FormSection>
              <FormSection>
                <FormLabel><BuildIcon sx={{ fontSize: 13 }} /> Task *</FormLabel>
                <StyledTextarea rows={2} placeholder="What should the candidate do..."
                  value={code.task}
                  onChange={e => setCode(p => ({ ...p, task: e.target.value }))} />
              </FormSection>
              <FormSection>
                <FormLabel>Expected Approach</FormLabel>
                <StyledTextarea rows={2} placeholder="Ideal approach or tools to use..."
                  value={code.expected_approach}
                  onChange={e => setCode(p => ({ ...p, expected_approach: e.target.value }))} />
              </FormSection>
            </>)}
          </>
        )}

        {/* Footer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end', mt: 1 }}>
          <Button onClick={requestCancel} disabled={saving} sx={{
            fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
            color: '#8B7DB5', border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`,
            textTransform: 'none', px: 2,
          }}>
            Cancel
          </Button>
          <Button
            disabled={!isValid() || saving}
            onClick={handleConfirm}
            sx={{
              background: isValid() && !saving
                ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#EEE',
              color: isValid() && !saving ? '#fff' : '#aaa',
              fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
              borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
              boxShadow: isValid() ? '0 4px 14px rgba(79,55,139,0.3)' : 'none',
            }}
          >
            {saving
              ? <CircularProgress size={16} sx={{ color: '#aaa', mr: 1 }} />
              : <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />}
            {saving ? 'Saving…' : 'Add Question'}
          </Button>
        </Box>
        </AddManualCard>
  </Dialog>

    {/* Discard-confirmation for a non-empty form */}
    <Dialog
      open={confirmDiscard}
      onClose={() => setConfirmDiscard(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <WarningAmberIcon sx={{ color: '#E65100', fontSize: 20 }} />
          <ReasonTitle>Discard this question?</ReasonTitle>
        </Box>
        <ReasonSubtitle>The details you entered will be lost.</ReasonSubtitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button onClick={() => setConfirmDiscard(false)} sx={{
            fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
            color: '#8B7DB5', border: '1.5px solid #D0BCFF', borderRadius: `${RADIUS.sm}px`,
            textTransform: 'none', px: 2,
          }}>
            Keep Editing
          </Button>
          <Button
            onClick={() => { setConfirmDiscard(false); onCancel(); }}
            sx={{
              background: 'linear-gradient(135deg, #B71C1C, #E53935)', color: '#fff',
              fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
              borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
            }}
          >
            Discard
          </Button>
        </Box>
      </Box>
    </Dialog>
    </>
);
};

// ─── Main Component ───────────────────────────────────────────────────────────
const QuestionDialog = ({ open, setOpen,jdId:jdId, mcqQuestions: mcqProp, codingQuestions: codingProp, subjectiveQuestions: subjectiveProp,onQuestionsUpdated, readOnly = false ,performedBy, role }) => {
  const [activeTab, setActiveTab] = useState('mcq');
  const [isEditMode, setIsEditMode] = useState(false);

  // Per-tab deleted sets: { mcq: Set, subjective: Set, coding: Set }
  // Indices into the CURRENT question props; questions removed on the server
  // but not yet refetched. Cleared whenever the props change (i.e. refetch).
  const [deletedSets, setDeletedSets] = useState(emptySets);
  // Per-tab selected sets for bulk delete
  const [selectedSets, setSelectedSets] = useState(emptySets);
  // Questions removed while this dialog is open (survives refetch; feeds Regenerate auto-fill)
  const [sessionRemoved, setSessionRemoved] = useState(emptyCounts);

  // Deletion log: [{ tab, questionPreview, reason, timestamp }]
  const [deletionLog, setDeletionLog]   = useState([]);
const [logLoading,  setLogLoading]    = useState(false);
const [logError,    setLogError]      = useState('');
const [logReloadKey, setLogReloadKey] = useState(0);
const [showRegenerate, setShowRegenerate] = useState(false);
const [regenerating,   setRegenerating]   = useState(false);

const [showAddManual, setShowAddManual] = useState(false);
const [savingManual,  setSavingManual]  = useState(false);
const [showImport, setShowImport] = useState(false);

  // Reason prompt state
  const [reasonPrompt, setReasonPrompt] = useState(null); // { indices: [], tab: '' }
  const [removing,     setRemoving]     = useState(false);
  const [removeError,  setRemoveError]  = useState('');

  // Synchronous in-flight guards (state updates are async, so a fast
  // double-click could otherwise fire the same request twice)
  const removingRef     = useRef(false);
  const regeneratingRef = useRef(false);
  const savingManualRef = useRef(false);

  // Result messages
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => {
    setSnack({ open: true, message, severity });
  }, []);

  const logActivity = useCallback(async (action, options = {}) => {
    if (!performedBy || !role) return;
    try {
      await fetch(process.env.REACT_APP_ACTIVITY_LOG_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          performedBy,
          role,
          targetId:   options.targetId   || null,
          targetName: options.targetName || null,
          jdId:       options.jdId       || jdId,
          metadata:   options.metadata   || {},
          // timestamp is set by the schema automatically
        }),
      });
    } catch (e) {
      console.warn('Activity log failed silently:', e.message);
    }
  }, [performedBy, role, jdId]);

  // Load the removal log for the current JD. The previous JD's log is cleared
  // first so it never shows under the wrong JD; stale responses are ignored.
  useEffect(() => {
    if (!open || !jdId) return undefined;
    let cancelled = false;
    setDeletionLog([]);
    setLogError('');
    setLogLoading(true);
    fetch(`${REMOVAL_BASE}/${jdId}/removal-log`)
        .then(async r => {
            if (!r.ok) {
                const body = await readJson(r);
                throw new Error(body.message || `HTTP ${r.status}`);
            }
            return r.json();
        })
        .then(data => {
            if (!cancelled) setDeletionLog(data.log || []);
        })
        .catch(err => {
            console.error('Failed to load removal log:', err);
            if (!cancelled) setLogError(`Failed to load removal log: ${err.message}`);
        })
        .finally(() => { if (!cancelled) setLogLoading(false); });
    return () => { cancelled = true; };
}, [open, jdId, logReloadKey]);

  // Fresh question props (refetch, or a different JD) invalidate local indices
  useEffect(() => {
    setDeletedSets(emptySets());
    setSelectedSets(emptySets());
  }, [mcqProp, subjectiveProp, codingProp]);

  // Switching JD resets all per-JD UI state
  useEffect(() => {
    setDeletedSets(emptySets());
    setSelectedSets(emptySets());
    setSessionRemoved(emptyCounts());
    setReasonPrompt(null);
    setRemoveError('');
  }, [jdId]);
  const getQuestions = useCallback((tab) => {
    if (tab === 'mcq') return mcqProp || [];
    if (tab === 'subjective') return subjectiveProp || [];
    if (tab === 'coding') return codingProp || [];
    return [];
  }, [mcqProp, subjectiveProp, codingProp]);


  // Live counts (excluding deleted)
  const liveCount = (tab) => {
    const qs = getQuestions(tab);
    const del = deletedSets[tab];
    return qs.filter((_, i) => !del.has(i)).length;
  };
  const removedCounts = sessionRemoved;


  const tabs = [
    { key: 'mcq',        label: 'MCQ',    icon: <QuizIcon />,     count: liveCount('mcq') },
    { key: 'subjective', label: 'Theory', icon: <MenuBookIcon />, count: liveCount('subjective') },
    { key: 'coding',     label: 'Coding', icon: <CodeIcon />,     count: liveCount('coding') },
    ...(!readOnly ? [{ key: 'log', label: 'Removed', icon: <HistoryIcon />, count: deletionLog.length }] : []),
  ];

  const handleClose = () => {
    if (removingRef.current) return; // don't close mid-removal
    // Local "removed" overlay is about to be dropped; make sure the parent's
    // arrays reflect the server so indices stay valid on reopen.
    const removedAny = Object.values(sessionRemoved).some(n => n > 0) ||
      Object.values(deletedSets).some(s => s.size > 0);
    if (removedAny && typeof onQuestionsUpdated === 'function') onQuestionsUpdated();
    setOpen(false);
    setActiveTab('mcq');
    setIsEditMode(false);
    setDeletedSets(emptySets());
    setSelectedSets(emptySets());
    setSessionRemoved(emptyCounts());
    setReasonPrompt(null);
    setRemoveError('');
  };

  const toggleEditMode = () => {
    setIsEditMode(v => !v);
    // clear selections when leaving edit mode
    setSelectedSets(emptySets());
  };

  // Select/deselect a single card
  const handleSelect = (index) => {
    setSelectedSets(prev => {
      const next = new Set(prev[activeTab]);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return { ...prev, [activeTab]: next };
    });
  };

  // Trigger reason prompt for given indices in current tab
  const handleDelete = (indices) => {
    if (removingRef.current) return;
    setRemoveError('');
    setReasonPrompt({ indices, tab: activeTab });
  };

  const cancelDelete = () => {
    if (removingRef.current) return;
    setReasonPrompt(null);
    setRemoveError('');
  };

  // Confirm deletion with reason.
  // The backend deletes by array position only (POST remove-question
  // { tab, questionIndex } → splice) and has no restore, so:
  //  - local indices are translated to server positions by subtracting
  //    questions already removed but not yet refetched,
  //  - each batch is sent in DESCENDING order so earlier deletes don't shift later ones,
  //  - every response is checked; on failure we stop and only mark/log what succeeded,
  //  - afterwards the parent refetches, which resets the local index sets.
  const confirmDelete = async (reason) => {
    if (!reasonPrompt || removingRef.current) return;
    const { indices, tab } = reasonPrompt;
    const qs = getQuestions(tab);
    const tabName = toTabName(tab);
    const alreadyDeleted = Array.from(deletedSets[tab]);
    const toServerIndex = (i) => i - alreadyDeleted.filter(d => d < i).length;

    const targets = [...new Set(indices)]
      .filter(i => i >= 0 && i < qs.length && !deletedSets[tab].has(i))
      .sort((a, b) => b - a);
    if (targets.length === 0) {
      setReasonPrompt(null);
      return;
    }

    const buildEntry = (i) => {
        const q = qs[i] || {};
        return {
            tab:             tabName,
            questionPreview: q.question || q.problem_statement || q.scenario || q.Question || `Question ${i + 1}`,
            reason,
            timestamp:       Date.now(),
            category:        q.category   || '',
            difficulty:      q.difficulty || '',
            skill:           q.skill      || '',
        };
    };

    removingRef.current = true;
    setRemoving(true);
    setRemoveError('');

    // ── 1. Remove from the JD's question array (descending order) ──
    const removed = [];
    let failure = null;
    for (const idx of targets) {
        try {
            const res = await fetch(`${REMOVAL_BASE}/${jdId}/remove-question`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ tab: tabName, questionIndex: toServerIndex(idx) }),
            });
            if (!res.ok) {
                const body = await readJson(res);
                throw new Error(body.message || `HTTP ${res.status}`);
            }
            removed.push(idx);
        } catch (err) {
            console.error(`Failed to remove question ${idx + 1}:`, err);
            failure = err;
            break;
        }
    }

    // ── 2. Log only what was actually removed ──
    const newEntries = removed.map(buildEntry);
    let logFailure = null;
    if (newEntries.length > 0) {
        try {
            const res = await fetch(`${REMOVAL_BASE}/${jdId}/removal-log`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ entries: newEntries }),
            });
            if (!res.ok) {
                const body = await readJson(res);
                throw new Error(body.message || `HTTP ${res.status}`);
            }
            setDeletionLog(prev => [...prev, ...newEntries]);
        } catch (err) {
            console.error('Failed to save removal log:', err);
            logFailure = err;
        }

        // ── 3. Update local UI state (overlay until the refetch lands) ──
        setDeletedSets(prev => {
            const next = new Set(prev[tab]);
            removed.forEach(i => next.add(i));
            return { ...prev, [tab]: next };
        });
        setSessionRemoved(prev => ({ ...prev, [tab]: prev[tab] + removed.length }));
        logActivity('REMOVE_QUESTIONS', {
          targetName: `Removed ${removed.length} question(s) from ${tab}`,
          metadata: {
            tab,
            count: removed.length,
            reason,
            questions: newEntries.map(e => e.questionPreview),
          },
        });

        // ── 4. Refetch so subsequent deletes use fresh server positions ──
        if (typeof onQuestionsUpdated === 'function') {
            try {
                await onQuestionsUpdated();
            } catch (err) {
                console.error('Failed to refresh questions:', err);
            }
        }
    }

    removingRef.current = false;
    setRemoving(false);

    if (failure && removed.length === 0) {
        // Nothing changed on the server — keep the prompt open so the user can retry
        setRemoveError(`Failed to remove question${targets.length > 1 ? 's' : ''}: ${failure.message}`);
        return;
    }

    setSelectedSets(prev => ({ ...prev, [tab]: new Set() }));
    setReasonPrompt(null);

    if (failure) {
        notify(`Removed ${removed.length} of ${targets.length} questions, then failed: ${failure.message}`, 'error');
    } else if (logFailure) {
        notify(`Removed ${removed.length} question(s), but saving the removal log failed: ${logFailure.message}`, 'warning');
    } else {
        notify(`Removed ${removed.length} question(s).`);
    }
};

  // Bulk delete selected
  const handleBulkDelete = () => {
    const sel = selectedSets[activeTab];
    if (sel.size === 0) return;
    handleDelete(Array.from(sel));
  };

  // Save: filter out deleted from all question arrays and call back
  const handleSave = () => {
    setIsEditMode(false);
    setSelectedSets({ mcq: new Set(), subjective: new Set(), coding: new Set() });
};
const handleDownloadLog = () => {
  logActivity('DOWNLOAD_REMOVAL_LOG', {
    metadata: { entriesCount: deletionLog.length },
  });
  if (!deletionLog.length) return;
  const logJson = JSON.stringify(deletionLog, null, 2);
  const blob = new Blob([logJson], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `removal_log_${jdId}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

const handleRegenerate = async ({ counts, skills, secSkills, language, experience }) => {
  if (regeneratingRef.current) return;
  regeneratingRef.current = true;
  setRegenerating(true);
  try {
    const REGEN_URL = process.env.REACT_APP_REGENERATE_SCREENING_QUES_API;

    const res = await fetch(`${REGEN_URL}`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        jdId:                jdId,
        skills:              skills,
        secondarySkills:     secSkills,
        experience:          experience ? Number(experience) : null,
        programmingLanguage: language   || null,
        count:               counts,
      }),
    });

    const data = await readJson(res);
    if (!res.ok) throw new Error(data.error || data.message || 'Regeneration failed');

    setShowRegenerate(false);
    if (typeof onQuestionsUpdated === 'function') onQuestionsUpdated();
    notify(`Added: ${data.appended?.mcq ?? 0} MCQ · ${data.appended?.subjective ?? 0} Theory · ${data.appended?.coding ?? 0} Coding`);
    logActivity('REGENERATE_QUESTIONS', {
      metadata: {
        counts, skills, secondarySkills: secSkills,
        language: language || null,
        experience: experience || null,
        appended: data.appended ?? null,
      },
    });
  } catch (err) {
    console.error('Regenerate failed:', err);
    notify(`Failed to regenerate questions: ${err.message}`, 'error');
  } finally {
    regeneratingRef.current = false;
    setRegenerating(false);
  }
};
const handleAddManual = async (type, questionData) => {
  if (savingManualRef.current) return;
  savingManualRef.current = true;
  setSavingManual(true);
  try {
    const tabMap = { mcq: 'Mcq', subjective: 'Subjective', coding: 'Coding' };

    const res = await fetch(`${REMOVAL_BASE}/${jdId}/add-question`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({
        tab:          tabMap[type],
        questionData: questionData,
      }),
    });

    const data = await readJson(res);

    if (!res.ok) throw new Error(data.message || data.error || 'Failed to add question');

    setShowAddManual(false);
    setActiveTab(type);
    if (typeof onQuestionsUpdated === 'function') onQuestionsUpdated();
    notify('Question added.');
    logActivity('ADD_MANUAL_QUESTION', {
      targetName: questionData.question || questionData.problem_statement 
                  || questionData.scenario || questionData.task || 'Manual question',
      metadata: {
        type,
        difficulty: questionData.difficulty || null,
        skill:      questionData.skill      || null,
        category:   questionData.category   || null,
      },
    });
  } catch (err) {
    console.error('Add manual question failed:', err);
    notify(`Failed to add question: ${err.message}`, 'error');
  } finally {
    savingManualRef.current = false;
    setSavingManual(false);
  }
};

  // Removed while the dialog has been open (deletedSets is cleared on every refetch)
  const totalRemoved = sessionRemoved.mcq + sessionRemoved.subjective + sessionRemoved.coding;
  const busy = removing || regenerating || savingManual;
  const currentSelected = selectedSets[activeTab]?.size || 0;

  // Filtered questions for PDF (exclude deleted)
  const pdfMcq     = (mcqProp || []).filter((_, i) => !deletedSets.mcq.has(i));
  const pdfSubj    = (subjectiveProp || []).filter((_, i) => !deletedSets.subjective.has(i));
  const pdfCoding  = (codingProp || []).filter((_, i) => !deletedSets.coding.has(i));

  const sharedProps = (tab) => ({
    isEditMode,
    deletedSet: deletedSets[tab],
    selectedSet: selectedSets[tab],
    onSelect: handleSelect,
    onDelete: handleDelete,
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: `${RADIUS.lg}px`,
            boxShadow: '0 20px 60px rgba(79,55,139,0.2)',
            background: '#fff',
            overflow: 'hidden',
          },
        }}
      >
        <DialogHeader>
          <Box>
            <DialogTitleText>Generated Questions</DialogTitleText>
            <DialogSubtitle>
              {liveCount('mcq') + liveCount('subjective') + liveCount('coding')} active questions
              {totalRemoved > 0 && ` · ${totalRemoved} removed`}
            </DialogSubtitle>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {!readOnly && (
            <Button
              onClick={toggleEditMode}
              startIcon={isEditMode ? <CloseIcon /> : <EditIcon />}
              sx={{
                background: isEditMode ? '#FFEBEE' : '#F3EEF9',
                color: isEditMode ? '#B71C1C' : '#6750A4',
                fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
                borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
                border: `1.5px solid ${isEditMode ? '#FFCDD2' : '#D0BCFF'}`,
                '&:hover': { background: isEditMode ? '#FFCDD2' : '#E8DEF8' },
              }}
            >
              {isEditMode ? 'Exit Edit' : 'Edit'}
            </Button>
             )}
              {!readOnly && (
            <Button
              onClick={() => setShowRegenerate(true)}
              disabled={busy}
              startIcon={<AutorenewIcon />}
              sx={{
                background: '#E8F5E9',
                color: '#2E7D32',
                fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
                borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
                border: '1.5px solid #A5D6A7',
                '&:hover': { background: '#C8E6C9' },
              }}
            >
              Regenerate
            </Button>
            )}
             {!readOnly && (
             <Button
            onClick={() => setShowAddManual(true)}
            disabled={busy}
            startIcon={<AddIcon />}
            sx={{
              background: '#E3F2FD',
              color: '#1565C0',
              fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
              borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
              border: '1.5px solid #90CAF9',
              '&:hover': { background: '#BBDEFB' },
            }}
          >
            Add Manual
          </Button>
          

        )}

        {!readOnly && (
  <Button
    onClick={() => setShowImport(true)}
    disabled={busy}
    startIcon={<UploadFileIcon />}
    sx={{
      background: '#FFF3E0',
      color: '#E65100',
      fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, height: 36,
      borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 2,
      border: '1.5px solid #FFCC80',
      '&:hover': { background: '#FFE0B2' },
    }}
  >
    Import Excel
  </Button>
)}
            <CloseBtn onClick={handleClose} disabled={removing} size="small"><CloseIcon /></CloseBtn>
          </Box>
        </DialogHeader>

        {/* Edit mode banner */}
        {isEditMode &&!readOnly && (
          <EditBanner>
            <EditBannerText>
              <EditIcon sx={{ fontSize: 16 }} />
              Edit mode — select questions to remove in bulk, or remove individually
              {currentSelected > 0 && ` · ${currentSelected} selected`}
            </EditBannerText>
            <EditBannerActions>
              {currentSelected > 0 && (
                <Button
                  onClick={handleBulkDelete}
                  disabled={removing}
                  startIcon={<DeleteSweepIcon />}
                  size="small"
                  sx={{
                    background: '#FFEBEE', color: '#B71C1C', fontFamily: FONT_BODY,
                    fontWeight: 600, fontSize: TYPE.caption, height: 32, borderRadius: `${RADIUS.sm}px`, textTransform: 'none',
                    border: '1.5px solid #FFCDD2', px: 1.5,
                    '&:hover': { background: '#FFCDD2' },
                  }}
                >
                  Remove {currentSelected} Selected
                </Button>
              )}
              {totalRemoved > 0 && (
                <Button
                  onClick={handleSave}
                  startIcon={<SaveIcon />}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #4F378B, #6750A4)', color: '#fff',
                    fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.caption, height: 32,
                    borderRadius: `${RADIUS.sm}px`, textTransform: 'none', px: 1.5,
                    boxShadow: '0 3px 10px rgba(79,55,139,0.3)',
                    '&:hover': { transform: 'translateY(-1px)' },
                  }}
                >
                  Done Editing
                </Button>
              )}
            </EditBannerActions>
          </EditBanner>
        )}

        <TabBar>
          {tabs.map(tab => (
            <Tab key={tab.key} $active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
              {tab.icon}
              {tab.label}
              <TabCount active={activeTab === tab.key}>
                {tab.key === 'log' ? tab.count : tab.count}
              </TabCount>
            </Tab>
          ))}
        </TabBar>

        <Box sx={{ mx: '24px', mt: '16px', height: '1px', background: 'linear-gradient(90deg, #6750A4, transparent)' }} />

        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <TabContent key={activeTab + isEditMode}>
            {activeTab === 'mcq' && (
              <MCQView questions={mcqProp} {...sharedProps('mcq')} />
            )}
            {activeTab === 'subjective' && (
              <SubjectiveView questions={subjectiveProp} {...sharedProps('subjective')} />
            )}
            {activeTab === 'coding' && (
              <CodingView questions={codingProp} {...sharedProps('coding')} />
            )}
            {activeTab === 'log' && (
              <DeletionLogView
                log={deletionLog}
                onDownload={handleDownloadLog}
                loading={logLoading}
                error={logError}
                onRetry={() => setLogReloadKey(k => k + 1)}
              />
            )}
          </TabContent>
        </DialogContent>

        <DialogActions disableSpacing sx={{ padding: '16px 24px', borderTop: '1px solid #F0EAF9', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
            {tabs.filter(t => t.key !== 'log').map(tab => (
              <Box key={tab.key} sx={{
                width: 8, height: 8, borderRadius: '50%',
                background: activeTab === tab.key ? 'linear-gradient(135deg, #4F378B, #6750A4)' : '#E8DEF8',
                transition: 'all 0.2s',
              }} />
            ))}
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: '#8B7DB5', fontWeight: 500, ml: 0.5 }}>
              {tabs.find(t => t.key === activeTab)?.label} — {tabs.find(t => t.key === activeTab)?.count} questions
            </Typography>
          </Box>

          <DownloadBtn
            startIcon={<DownloadIcon />}
            onClick={() => {
              generatePdf(pdfMcq, pdfSubj, pdfCoding);
              logActivity('DOWNLOAD_QUESTIONS_PDF', {
                metadata: {
                  mcqCount:    pdfMcq.length,
                  theoryCount: pdfSubj.length,
                  codingCount: pdfCoding.length,
                },
              });
            }}
          >
            Download PDF
          </DownloadBtn>

          <Button
            onClick={handleClose}
            disabled={removing}
            sx={{
              background: 'linear-gradient(135deg, #6750A4, #4F378B) !important',
              color: '#fff !important',
              fontFamily: `${FONT_BODY} !important`,
              fontWeight: '600 !important',
              fontSize: `${TYPE.small}px !important`,
              borderRadius: `${RADIUS.sm}px !important`,
              height: 36,
              padding: '0 16px !important',
              textTransform: 'none !important',
              boxShadow: '0 4px 14px rgba(79,55,139,0.3) !important',
              '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 7px 20px rgba(79,55,139,0.4) !important' },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reason prompt overlay */}
      {reasonPrompt && (
        <ReasonPrompt
          count={reasonPrompt.indices.length}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          removing={removing}
          error={removeError}
        />
      )}
      {showRegenerate && (
  <RegeneratePrompt
    removedCounts={removedCounts}
    onConfirm={handleRegenerate}
    onCancel={() => setShowRegenerate(false)}
    loading={regenerating}
  />
)}
{showAddManual && (
  <AddManualPrompt
    activeTab={activeTab}
    onConfirm={handleAddManual}
    onCancel={() => setShowAddManual(false)}
    saving={savingManual}
  />
)}

{showImport && (
  <ImportQuestionsModal
    jdId={jdId}
    existing={{
      mcq:        mcqProp || [],
      subjective: subjectiveProp || [],
      coding:     codingProp || [],
    }}
    logActivity={logActivity}
    onImported={() => { if (typeof onQuestionsUpdated === 'function') onQuestionsUpdated(); }}
    onClose={() => setShowImport(false)}
  />
)}

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSnack(s => ({ ...s, open: false })); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default QuestionDialog;